"use client";

import { CheckIcon } from "@heroicons/react/20/solid";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useVirtualizer } from "@tanstack/react-virtual";
import clsx from "clsx";
import Link from "next/link";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import {
  ComponentPropsWithoutRef,
  CSSProperties,
  MouseEventHandler,
  ReactElement,
  ReactNode,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { PopoverProps } from "react-tiny-popover";
import Button, { BUTTON_STYLE, ButtonProps } from "./Button";
import DropdownMenu from "./DropdownMenu";
import DropdownMenuDivider from "./DropdownMenuDivider";
import PopoverPannel from "./PopoverPannel";

export type InputOption<T> = {
  label?: string | ReactElement;
  icon?: ReactElement;
  value: T;
  tooltip?: string;
  trailing?: ReactElement;
  category?: string;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  hoverPopoverContent?: ReactElement;
  href?: string;
};

export type SelectInputProps<T extends string | number | null> = {
  options: InputOption<T>[];
  value: T;
  onValueChange: (value: T) => void;
  size?: ButtonProps["size"];
  variant?: ButtonProps["variant"];
  selectedLabel?: boolean;
  optionsLabel?: boolean;
  onButtonClick?: MouseEventHandler<HTMLButtonElement>;
  contentHeader?: ReactElement;
  /** Optional actions below the options, including nested selects. */
  contentFooter?: (helpers: { close: () => void }) => ReactNode;
  /** Replaces the default button while retaining dropdown behavior. */
  trigger?: ReactElement;
  virtualize?: boolean | number; // true or threshold number
  itemSizePx?: number;
  maxHeightPx?: number;
  onVisibleValuesChange?: (values: T[]) => void;
  positions?: PopoverProps["positions"];
  align?: PopoverProps["align"];
  buttonLabel?: ReactNode;
  buttonAlign?: ButtonProps["align"];
  buttonTrailing?: ReactNode;
  disabled?: boolean;
  buttonLeading?: ReactElement | null;
  showChevron?: boolean;
  allowReselect?: boolean;
  dropdownContainerClassName?: string;
  showOptionCheckmark?: boolean;
} & ComponentPropsWithoutRef<"div">;

const OptionButton = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps & { href?: string }
>(({ href, children, ...buttonProps }, ref) => {
  if (href && !buttonProps.disabled) {
    return (
      <Button asChild ref={ref as any} {...buttonProps}>
        <Link href={href}>{children}</Link>
      </Button>
    );
  }
  return (
    <Button ref={ref as any} {...buttonProps}>
      {children}
    </Button>
  );
});
OptionButton.displayName = "OptionButton";

const SelectInput = <T extends string | number | null>({
  options,
  value,
  onValueChange,
  size = "m",
  variant = "secondary",
  selectedLabel = true,
  optionsLabel = true,
  onButtonClick,
  contentHeader,
  contentFooter,
  trigger,
  virtualize,
  itemSizePx,
  maxHeightPx = 320,
  onVisibleValuesChange,
  positions = ["bottom", "top"],
  align = "end",
  buttonLabel,
  buttonAlign,
  buttonTrailing,
  buttonLeading,
  showChevron = true,
  allowReselect = false,
  dropdownContainerClassName,
  showOptionCheckmark = true,
  className,
  disabled = false,
  ...rest
}: SelectInputProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);

  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const optionsRefs = useRef<(HTMLButtonElement | HTMLAnchorElement | null)[]>(
    [],
  );
  const parentRef = useRef<HTMLDivElement>(null);
  const contentHeaderRef = useRef<HTMLDivElement>(null);
  const focusChangeCauseRef = useRef<"keyboard" | "mouse" | null>(null);

  const selectedOption =
    options.find((option) => option.value === value) || options[0];

  const groupedOptions = useMemo(
    () => groupOptionsByCategory(options),
    [options],
  );
  const flattenedOptions = useMemo(
    () => flattenGroupedOptions(groupedOptions),
    [groupedOptions],
  );
  const totalOptions = flattenedOptions.length;
  const selectedIndex = useMemo(
    () => flattenedOptions.findIndex((option) => option.value === value),
    [flattenedOptions, value],
  );
  const fallbackFocusedIndex =
    selectedIndex === -1 ? (totalOptions ? 0 : -1) : selectedIndex;
  const [focusedIndex, setFocusedIndex] =
    useState<number>(fallbackFocusedIndex);
  optionsRefs.current.length = totalOptions;

  const shouldVirtualize =
    typeof virtualize === "number"
      ? flattenedOptions.length > virtualize
      : !!virtualize;
  const SIZE_TO_PX: Record<NonNullable<ButtonProps["size"]>, number> = {
    s: parseInt(BUTTON_STYLE.s.height, 10) || 32,
    m: parseInt(BUTTON_STYLE.m.height, 10) || 36,
    l: parseInt(BUTTON_STYLE.l.height, 10) || 48,
  } as any;
  const itemSize = itemSizePx ?? SIZE_TO_PX[size ?? "m"] ?? 36;
  const rowVirtualizer = useVirtualizer({
    count: flattenedOptions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => itemSize,
    overscan: 10,
  });
  const virtualItems = rowVirtualizer.getVirtualItems();

  useEffect(() => {
    setFocusedIndex((prev) => {
      if (totalOptions === 0) {
        return -1;
      }
      if (prev === -1) {
        return fallbackFocusedIndex;
      }
      if (prev >= totalOptions) {
        return totalOptions - 1;
      }
      return prev;
    });
  }, [fallbackFocusedIndex, totalOptions]);

  useEffect(() => {
    if (!isOpen) {
      setFocusedIndex(fallbackFocusedIndex);
    }
  }, [fallbackFocusedIndex, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (focusedIndex < 0) return;
    if (focusChangeCauseRef.current !== "keyboard") return;

    const target = optionsRefs.current[focusedIndex];
    if (target) {
      target.focus();
      focusChangeCauseRef.current = null;
      return;
    }

    if (shouldVirtualize) {
      try {
        rowVirtualizer.scrollToIndex(focusedIndex, { align: "auto" });
      } catch (_) {
        // no-op
      }
    }
  }, [focusedIndex, isOpen, rowVirtualizer, shouldVirtualize, virtualItems]);

  useEffect(() => {
    if (!isOpen) return;
    if (focusChangeCauseRef.current !== "keyboard") return;
    setHoverIndex(focusedIndex >= 0 ? focusedIndex : null);
  }, [focusedIndex, isOpen]);

  // Notify parent of visible option values after render, when virtualizer updates
  const lastVisibleValuesRef = useRef<T[] | null>(null);
  useEffect(() => {
    if (!isOpen || !onVisibleValuesChange) return;
    try {
      const vis = virtualItems.map(
        (vr) => flattenedOptions[vr.index]?.value,
      ) as T[];

      const prev = lastVisibleValuesRef.current;
      const changed =
        !prev ||
        prev.length !== vis.length ||
        prev.some((v, i) => v !== vis[i]);
      if (!changed) return;

      lastVisibleValuesRef.current = vis;
      onVisibleValuesChange(vis);
    } catch (_) {
      // no-op
    }
  }, [isOpen, onVisibleValuesChange, virtualItems, flattenedOptions]);

  useEffect(() => {
    if (!isOpen || !contentHeader) return;
    const frame = requestAnimationFrame(() => {
      const input = contentHeaderRef.current?.querySelector("input");
      input?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [isOpen, !!contentHeader]);

  useEffect(() => {
    if (!isOpen || selectedIndex < 0) return;

    const frame = requestAnimationFrame(() => {
      if (shouldVirtualize) {
        try {
          rowVirtualizer.scrollToIndex(selectedIndex, { align: "center" });
        } catch (_) {
          // no-op
        }
      } else {
        optionsRefs.current[selectedIndex]?.scrollIntoView({
          block: "nearest",
        });
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [isOpen, selectedIndex, shouldVirtualize, rowVirtualizer]);

  return (
    <DropdownMenu
      size={size}
      containerClassName={clsx(
        optionsLabel ? "max-w-sm" : "!max-w-[186px]",
        dropdownContainerClassName,
      )}
      positions={positions}
      align={align}
      open={isOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          setHoverIndex(null);
          focusChangeCauseRef.current = null;
        }
        setIsOpen(nextOpen);
      }}
    >
      <DropdownMenu.Trigger>
        {trigger ?? (
          <Button
            className={clsx(className)}
            align={buttonAlign ?? (selectedLabel ? "start" : "center")}
            ref={buttonRef}
            size={size}
            variant={variant}
            onClick={(event) => {
              if (onButtonClick) {
                onButtonClick(event);
              }
            }}
            disabled={disabled}
            data-entity-control=""
            data-tooltip-id={rest["data-tooltip-id"]}
            data-tooltip-content={rest["data-tooltip-content"]}
          >
            {buttonLeading !== undefined ? buttonLeading : selectedOption?.icon}
            {selectedLabel ? (buttonLabel ?? selectedOption?.label) : null}
            {buttonTrailing && <Button.Content>{buttonTrailing}</Button.Content>}
            {showChevron &&
              (selectedLabel || (!selectedLabel && !selectedOption.icon)) && (
                <ChevronDownIcon />
              )}
          </Button>
        )}
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        disablePadding
        className={clsx(
          optionsLabel && shouldVirtualize ? "flex flex-col" : undefined,
        )}
      >
        {({
          paddingClassName,
          close,
          focusFirstItem,
          focusLastItem,
          focusNextItem,
          focusPreviousItem,
        }) => {
          const syncFocusedIndex = (nextIndex: number) => {
            setFocusedIndex((prev) => (prev === nextIndex ? prev : nextIndex));
          };

          const moveFocus = (direction: "next" | "prev") => {
            if (!totalOptions) return;
            focusChangeCauseRef.current = "keyboard";
            setFocusedIndex((prev) => {
              if (prev === -1) {
                return direction === "next" ? 0 : totalOptions - 1;
              }
              return direction === "next"
                ? (prev + 1) % totalOptions
                : (prev - 1 + totalOptions) % totalOptions;
            });
            if (!shouldVirtualize) {
              if (direction === "next") {
                focusNextItem();
              } else {
                focusPreviousItem();
              }
            }
          };

          const focusEdge = (edge: "first" | "last") => {
            if (!totalOptions) return;
            focusChangeCauseRef.current = "keyboard";
            setFocusedIndex(edge === "first" ? 0 : totalOptions - 1);
            if (!shouldVirtualize) {
              if (edge === "first") {
                focusFirstItem();
              } else {
                focusLastItem();
              }
            }
          };

          const handleMenuKeyDown = (
            event: ReactKeyboardEvent<HTMLElement>,
          ) => {
            if (!isOpen || !totalOptions) return;
            // The menu owns focus traversal when footer actions extend the
            // option list; those actions have no index in flattenedOptions.
            if (contentFooter && !shouldVirtualize) return;
            switch (event.key) {
              case "ArrowDown":
              case "ArrowRight":
                event.preventDefault();
                event.stopPropagation();
                moveFocus("next");
                break;
              case "ArrowUp":
              case "ArrowLeft":
                event.preventDefault();
                event.stopPropagation();
                moveFocus("prev");
                break;
              case "Home":
                event.preventDefault();
                event.stopPropagation();
                focusEdge("first");
                break;
              case "End":
                event.preventDefault();
                event.stopPropagation();
                focusEdge("last");
                break;
              default:
                break;
            }
          };

          return optionsLabel && shouldVirtualize ? (
            <div
              style={{ maxHeight: `${maxHeightPx}px` }}
              className="flex flex-col"
              onKeyDown={handleMenuKeyDown}
            >
              {contentHeader ? (
                <div ref={contentHeaderRef} className="w-full">{contentHeader}</div>
              ) : null}
              <div
                ref={parentRef}
                className={clsx("overflow-y-auto", paddingClassName)}
                style={{ height: `${maxHeightPx}px` }}
              >
                <div
                  style={{
                    height: `${rowVirtualizer.getTotalSize()}px`,
                    width: "100%",
                    position: "relative",
                  }}
                >
                  {virtualItems.map((virtualRow) => {
                    const option = flattenedOptions[virtualRow.index];
                    const flatIndex = virtualRow.index;
                    const handleOptionClick = () => {
                      if (!option.href) {
                        if (allowReselect || option.value !== value) {
                          onValueChange(option.value);
                        }
                      }
                      close();
                    };
                    const handleOptionFocus = () => {
                      syncFocusedIndex(flatIndex);
                    };
                    return (
                      <div
                        key={option.value as any}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: `${virtualRow.size}px`,
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                      >
                        {option.hoverPopoverContent ? (
                          <PopoverPannel
                            size={size === "s" ? "s" : "m"}
                            positions={["right"]}
                            align="start"
                            isOpen={hoverIndex === flatIndex}
                            onClickOutside={() => setHoverIndex(null)}
                            content={<div>{option.hoverPopoverContent}</div>}
                            containerClassName="min-w-0"
                          >
                            <div>
                              <OptionButton
                                href={option.href}
                                align="start"
                                data-entity-control
                                data-tooltip-id="global"
                                data-tooltip-html={option.tooltip}
                                data-tooltip-place={"right"}
                                data-tooltip-offset={12}
                                data-tooltip-content={option.tooltip}
                                onMouseMove={() => {
                                  focusChangeCauseRef.current = "mouse";
                                  setFocusedIndex(flatIndex);
                                }}
                                onMouseEnter={() => setHoverIndex(flatIndex)}
                                onMouseLeave={() => setHoverIndex(null)}
                                ref={(el) => {
                                  optionsRefs.current[flatIndex] = el;
                                }}
                                size={size}
                                variant={"tertiary"}
                                onClick={handleOptionClick}
                                disabled={option.disabled}
                                style={option.style}
                                className={clsx(
                                  "w-full group",
                                  option.className ?? "",
                                )}
                                onFocus={handleOptionFocus}
                              >
                                {option.icon}
                                {option.label}
                                {option.trailing && option.value !== value ? (
                                  <Button.Content>
                                    {option.trailing}
                                  </Button.Content>
                                ) : null}
                                {showOptionCheckmark ? (
                                  option.value === value ? (
                                    <CheckIcon />
                                  ) : (
                                    <div />
                                  )
                                ) : null}
                              </OptionButton>
                            </div>
                          </PopoverPannel>
                        ) : (
                          <OptionButton
                            href={option.href}
                            align="start"
                            data-entity-control
                            data-tooltip-id="global"
                            data-tooltip-html={option.tooltip}
                            data-tooltip-place={"right"}
                            data-tooltip-offset={12}
                            data-tooltip-content={option.tooltip}
                            onMouseMove={() => {
                              focusChangeCauseRef.current = "mouse";
                              setFocusedIndex(flatIndex);
                            }}
                            onMouseEnter={() => setHoverIndex(flatIndex)}
                            onMouseLeave={() => setHoverIndex(null)}
                            ref={(el) => {
                              optionsRefs.current[flatIndex] = el;
                            }}
                            size={size}
                            variant={"tertiary"}
                            onClick={handleOptionClick}
                            disabled={option.disabled}
                            style={option.style}
                            className={clsx(
                              "w-full group",
                              option.className ?? "",
                            )}
                            onFocus={handleOptionFocus}
                          >
                            {option.icon}
                            {option.label}
                            {option.trailing && option.value !== value ? (
                              <Button.Content>{option.trailing}</Button.Content>
                            ) : null}
                            {showOptionCheckmark ? (
                              option.value === value ? (
                                <CheckIcon />
                              ) : (
                                <div />
                              )
                            ) : null}
                          </OptionButton>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              {contentFooter ? (
                <div className={paddingClassName}>{contentFooter({ close })}</div>
              ) : null}
            </div>
          ) : (
            <ul
              className={clsx("overflow-auto max-h-80", paddingClassName)}
              onKeyDown={handleMenuKeyDown}
            >
              {contentHeader ? (
                <li ref={contentHeaderRef as any} className="sticky top-0 bg-background dark:bg-surface z-10">
                  {contentHeader}
                </li>
              ) : null}
              {Object.keys(groupedOptions).map((category, catIndex) => (
                <li key={category}>
                  {category !== "default" && catIndex !== 0 && (
                    <DropdownMenuDivider size={size === "s" ? "s" : "m"} />
                  )}
                  <ul className={optionsLabel ? "" : "flex flex-wrap gap-1"}>
                    {groupedOptions[category].map((option) => {
                      const flatIndex = flattenedOptions.findIndex(
                        (flatOption) => flatOption.value === option.value,
                      );
                      const handleOptionClick = () => {
                        if (!option.href) {
                          if (allowReselect || option.value !== value) {
                            onValueChange(option.value);
                          }
                        }
                        close();
                      };
                      const handleOptionFocus = () => {
                        syncFocusedIndex(flatIndex);
                      };
                      return (
                        <li key={option.value}>
                          {option.hoverPopoverContent ? (
                            <PopoverPannel
                              size={size === "s" ? "s" : "m"}
                              positions={["right"]}
                              align="start"
                              isOpen={hoverIndex === flatIndex}
                              onClickOutside={() => setHoverIndex(null)}
                              content={<div>{option.hoverPopoverContent}</div>}
                              containerClassName="min-w-0"
                            >
                              <div>
                                <OptionButton
                                  href={option.href}
                                  align={optionsLabel ? "start" : "center"}
                                  data-entity-control
                                  key={option.value}
                                  data-tooltip-id="global"
                                  data-tooltip-html={option.tooltip}
                                  data-tooltip-place={
                                    optionsLabel ? "right" : "top"
                                  }
                                  data-tooltip-offset={12}
                                  data-tooltip-content={
                                    optionsLabel
                                      ? option.tooltip
                                      : typeof option.label === "string"
                                        ? option.label
                                        : undefined
                                  }
                                  onMouseMove={() => {
                                    focusChangeCauseRef.current = "mouse";
                                    setFocusedIndex(flatIndex);
                                  }}
                                  onMouseEnter={() => setHoverIndex(flatIndex)}
                                  onMouseLeave={() => setHoverIndex(null)}
                                  ref={(el) => {
                                    optionsRefs.current[flatIndex] = el;
                                  }}
                                  size={size}
                                  variant={
                                    optionsLabel
                                      ? "tertiary"
                                      : option.value === value
                                        ? "flat"
                                        : "tertiary"
                                  }
                                  onClick={handleOptionClick}
                                  disabled={option.disabled}
                                  style={option.style}
                                  className={clsx(
                                    `${option.className ?? ""}`,
                                    optionsLabel ? "w-full" : "",
                                  )}
                                  onFocus={handleOptionFocus}
                                >
                                  {option.icon}
                                  {optionsLabel ? option.label : null}
                                  {option.trailing ? (
                                    <Button.Content>
                                      {option.trailing}
                                    </Button.Content>
                                  ) : null}
                                  {optionsLabel && showOptionCheckmark ? (
                                    option.value === value ? (
                                      <CheckIcon />
                                    ) : (
                                      <div />
                                    )
                                  ) : null}
                                </OptionButton>
                              </div>
                            </PopoverPannel>
                          ) : (
                            <OptionButton
                              href={option.href}
                              align={optionsLabel ? "start" : "center"}
                              data-entity-control
                              key={option.value}
                              data-tooltip-id="global"
                              data-tooltip-html={option.tooltip}
                              data-tooltip-place={
                                optionsLabel ? "right" : "top"
                              }
                              data-tooltip-offset={12}
                              data-tooltip-content={
                                optionsLabel
                                  ? option.tooltip
                                  : typeof option.label === "string"
                                    ? option.label
                                    : undefined
                              }
                              onMouseMove={() => {
                                focusChangeCauseRef.current = "mouse";
                                setFocusedIndex(flatIndex);
                              }}
                              onMouseEnter={() => setHoverIndex(flatIndex)}
                              onMouseLeave={() => setHoverIndex(null)}
                              ref={(el) => {
                                optionsRefs.current[flatIndex] = el;
                              }}
                              size={size}
                              variant={
                                optionsLabel
                                  ? "tertiary"
                                  : option.value === value
                                    ? "flat"
                                    : "tertiary"
                              }
                              onClick={handleOptionClick}
                              disabled={option.disabled}
                              style={option.style}
                              className={clsx(
                                `${option.className ?? ""}`,
                                optionsLabel ? "w-full" : "",
                              )}
                              onFocus={handleOptionFocus}
                            >
                              {option.icon}
                              {optionsLabel ? option.label : null}
                              {option.trailing ? (
                                <Button.Content>
                                  {option.trailing}
                                </Button.Content>
                              ) : null}
                              {optionsLabel && showOptionCheckmark ? (
                                option.value === value ? (
                                  <CheckIcon />
                                ) : (
                                  <div />
                                )
                              ) : null}
                            </OptionButton>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
              {contentFooter ? <li>{contentFooter({ close })}</li> : null}
            </ul>
          );
        }}
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};

export default SelectInput;

const groupOptionsByCategory = <T extends string | number | null>(
  options: InputOption<T>[],
) => {
  const groupedOptions: { [key: string]: InputOption<T>[] } = {};
  options.forEach((option) => {
    const category = option.category || "default";
    if (!groupedOptions[category]) {
      groupedOptions[category] = [];
    }
    groupedOptions[category].push(option);
  });
  return groupedOptions;
};

const flattenGroupedOptions = <
  T extends string | number | null,
>(groupedOptions: {
  [key: string]: InputOption<T>[];
}): InputOption<T>[] => {
  return Object.values(groupedOptions).flat();
};
